/**
 * 스켈레톤 UI 컴포넌트
 *
 * 데이터 로딩 중에 표시할 자리 표시자(placeholder) UI입니다.
 * 실제 콘텐츠가 로드되는 동안 사용자에게 로딩 상태를 시각적으로 전달합니다.
 *
 * 제공되는 컴포넌트:
 * - AppSkeleton: 기본 스켈레톤 요소
 * - AppPlaylistCardSkeleton: 플레이리스트 카드용
 * - AppTrackListSkeleton: 트랙 리스트용
 * - AppTableRowSkeleton: 테이블 행용
 * - AppDetailPageSkeleton: 상세 페이지용
 *
 * @component
 * @example
 * // 기본 스켈레톤
 * <AppSkeleton height="h-4" width="w-2/3" />
 *
 * @example
 * // 플레이리스트 로딩
 * <AppPlaylistCardSkeleton />
 *
 * @example
 * // 트랙 리스트 로딩
 * <AppTrackListSkeleton count={10} />
 */

'use client';

export interface AppSkeletonProps {
  className?: string;
  count?: number;
  height?: string;
  width?: string;
  circle?: boolean;
}

/**
 * 기본 스켈레톤 요소
 *
 * @typedef {object} AppSkeletonProps
 * @property {string} [className] - 추가 CSS 클래스
 * @property {number} [count=1] - 반복 표시할 개수 (여러 개일 때)
 * @property {string} [height='h-4'] - 높이 (TailwindCSS 클래스)
 * @property {string} [width='w-full'] - 너비 (TailwindCSS 클래스)
 * @property {boolean} [circle=false] - 원형 스켈레톤 여부
 *
 * @returns {JSX.Element} 애니메이션이 적용된 스켈레톤 요소
 *
 * @example
 * // 텍스트 라인 스켈레톤
 * <AppSkeleton height="h-4" width="w-3/4" />
 *
 * // 원형 스켈레톤 (아바타 등)
 * <AppSkeleton height="h-10" width="w-10" circle />
 *
 * // 여러 개 표시
 * <AppSkeleton count={3} height="h-4" className="mb-2" />
 */
export function AppSkeleton({
  className = '',
  count = 1,
  height = 'h-4',
  width = 'w-full',
  circle = false,
}: AppSkeletonProps) {
  return (
    <>
      {Array(count)
        .fill(null)
        .map((_, i) => (
          <div
            key={i}
            className={`
              bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200
              animate-pulse
              ${height}
              ${width}
              ${circle ? 'rounded-full' : 'rounded'}
              ${className}
              ${count > 1 ? 'mb-2' : ''}
            `}
          />
        ))}
    </>
  );
}

/**
 * 플레이리스트 카드 AppSkeleton
 */
export function AppPlaylistCardSkeleton() {
  return (
    <div className="p-4 border border-neutral-200 rounded-lg">
      <AppSkeleton height="h-6" width="w-2/3" className="mb-2" />
      <AppSkeleton height="h-4" width="w-1/2" className="mb-4" />
      <div className="flex gap-2">
        <AppSkeleton height="h-5" width="w-16" circle={false} className="rounded-full" />
        <AppSkeleton height="h-5" width="w-16" circle={false} className="rounded-full" />
      </div>
    </div>
  );
}

/**
 * 트랙 리스트 AppSkeleton
 */
export function AppTrackListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array(count)
        .fill(null)
        .map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-3 border border-neutral-200 rounded">
            <AppSkeleton height="h-4" width="w-8" circle={true} />
            <div className="flex-1">
              <AppSkeleton height="h-4" width="w-3/4" className="mb-2" />
              <AppSkeleton height="h-3" width="w-1/2" />
            </div>
            <AppSkeleton height="h-4" width="w-12" />
          </div>
        ))}
    </div>
  );
}

/**
 * 테이블 행 AppSkeleton
 */
export function AppTableRowSkeleton({ columns = 4, count = 5 }: { columns?: number; count?: number }) {
  return (
    <>
      {Array(count)
        .fill(null)
        .map((_, rowIdx) => (
          <tr key={rowIdx} className="border-b border-neutral-200">
            {Array(columns)
              .fill(null)
              .map((_, colIdx) => (
                <td key={colIdx} className="px-4 py-3">
                  <AppSkeleton height="h-4" width="w-full" />
                </td>
              ))}
          </tr>
        ))}
    </>
  );
}

/**
 * 상세 페이지 AppSkeleton
 */
export function AppDetailPageSkeleton() {
  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <AppSkeleton height="h-8" width="w-3/4" className="mb-4" />
        <AppSkeleton height="h-4" width="w-full" className="mb-2" />
        <AppSkeleton height="h-4" width="w-full" />
      </div>

      {/* Content sections */}
      <div className="space-y-6">
        {Array(3)
          .fill(null)
          .map((_, i) => (
            <div key={i}>
              <AppSkeleton height="h-6" width="w-1/3" className="mb-3" />
              <AppSkeleton height="h-4" width="w-full" className="mb-2" count={2} />
            </div>
          ))}
      </div>
    </div>
  );
}

export default AppSkeleton;
